/** One media slot after normalization. */
export interface NormalizedSrcSlot {
  type: string;
  url?: string;
  data?: unknown;
  size?: { width: number; height: number };
  texture?: { type: string; url: string; size?: { width: number; height: number } } | Array<{ type: string; url: string; size?: { width: number; height: number } }>;
}

const SLOT_DEFAULT_TYPE: Record<string, string> = {
  image: 'png',
  json: 'json',
  tex: 'tex',
  ske: 'ske',
  audio: 'audio',
  video: 'video',
};

const EXT_TO_TYPE: Record<string, string> = {
  png: 'png',
  jpg: 'jpg',
  jpeg: 'jpeg',
  webp: 'webp',
  json: 'json',
  tex: 'tex',
  ske: 'ske',
  mp3: 'audio',
  wav: 'audio',
  aac: 'audio',
  ogg: 'audio',
  mp4: 'video',
  webm: 'video',
  glb: 'glb',
  gltf: 'gltf',
};

function extensionOf(url: string): string {
  const path = url.split('?')[0].split('#')[0];
  const dot = path.lastIndexOf('.');
  if (dot < 0) return '';
  return path.slice(dot + 1).toLowerCase();
}

function typeFromUrl(url: string, slot: string): string {
  return EXT_TO_TYPE[extensionOf(url)] || SLOT_DEFAULT_TYPE[slot] || 'png';
}

function defaultSlotForResourceType(resourceType?: string): string {
  if (resourceType === 'AUDIO') return 'audio';
  if (resourceType === 'VIDEO') return 'video';
  return 'image';
}

function wrapSlot(slot: string, value: unknown, resourceName: string): NormalizedSrcSlot {
  if (typeof value === 'string') {
    const url = value.trim();
    if (!url) {
      throw missingUrlError(resourceName, slot);
    }
    return { type: typeFromUrl(url, slot), url };
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(
      `Resource "${resourceName}" src.${slot} must be a url string or { type, url }.`,
    );
  }
  const slotValue = value as NormalizedSrcSlot;
  if (slotValue.type === 'data') {
    return slotValue;
  }
  const url = typeof slotValue.url === 'string' ? slotValue.url.trim() : '';
  if (!url) {
    throw missingUrlError(resourceName, slot);
  }
  return {
    ...slotValue,
    type: slotValue.type || typeFromUrl(url, slot),
    url,
  };
}

export function missingUrlError(resourceName: string, slot: string): Error {
  return new Error(
    `Resource "${resourceName}" src.${slot}.url is required. ` +
      `Use { type, url } (e.g. { type: "webp", url: "https://..." }). ` +
      `A flat path string is also accepted and will be normalized.`,
  );
}

/**
 * Accept the documented `{ type, url }` slots, plus the common guesses
 * `src: "file.png"` and `src: { image: "a.webp", json: "a.json" }`.
 */
export function normalizeResourceSrc(
  src: unknown,
  resourceType?: string,
  resourceName = '(unnamed)',
): Record<string, NormalizedSrcSlot> {
  if (src == null) {
    throw new Error(`Resource "${resourceName}" src is required.`);
  }

  if (typeof src === 'string') {
    const slot = defaultSlotForResourceType(resourceType);
    return { [slot]: wrapSlot(slot, src, resourceName) };
  }

  if (typeof src !== 'object' || Array.isArray(src)) {
    throw new Error(
      `Resource "${resourceName}" src must be a url string or a slot object.`,
    );
  }

  const out: Record<string, NormalizedSrcSlot> = {};
  for (const [slot, value] of Object.entries(src as Record<string, unknown>)) {
    if (value == null) continue;
    out[slot] = wrapSlot(slot, value, resourceName);
  }
  if (Object.keys(out).length === 0) {
    throw new Error(`Resource "${resourceName}" src has no loadable slots.`);
  }
  return out;
}
